/**
 * Spin to Win Exit-Intent Popup
 * Version: 1.0.0
 *
 * Custom web component for gamified email capture with spinning wheel
 *
 * Features:
 * - Exit intent detection (desktop/mobile)
 * - Cookie-based session management
 * - Weighted prize selection
 * - Google Analytics integration
 * - Canvas confetti animation
 * - Accessibility support
 */

class SpinToWinPopup extends HTMLElement {
  constructor() {
    super();

    // State
    this.wheelUnlocked = false;
    this.isSpinning = false;
    this.hasShown = false;

    // Elements (will be set in connectedCallback)
    this.wheelContainer = null;
    this.spinButton = null;
    this.resultModal = null;
    this.lockOverlay = null;
    this.formContainer = null;
    this.unlockedMessage = null;

    // Settings
    this.enabled = this.dataset.spinEnabled === 'true';
    this.preview = this.dataset.preview === 'true';
    this.delayDesktop = parseInt(this.dataset.delayDesktop) || 0;
    this.delayMobile = parseInt(this.dataset.delayMobile) || 30;
    this.cooldownDays = parseInt(this.dataset.cooldownDays) || 30;
    this.enableAnalytics = this.dataset.enableAnalytics === 'true';

    // Bind methods
    this.init = this.init.bind(this);
    this.show = this.show.bind(this);
    this.close = this.close.bind(this);
    this.handleSpin = this.handleSpin.bind(this);
  }

  connectedCallback() {
    // Set element references
    this.wheelContainer = this.querySelector('[data-wheel-container]');
    this.spinButton = this.querySelector('[data-spin-button]');
    this.resultModal = this.querySelector('[data-result-modal]');
    this.lockOverlay = this.querySelector('[data-lock-overlay]');
    this.formContainer = this.querySelector('[data-form-container]');
    this.unlockedMessage = this.querySelector('[data-unlocked-message]');

    // Initialize after elements are ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init);
    } else {
      this.init();
    }
  }

  /**
   * Initialize the popup
   */
  init() {
    if (!this.shouldShow()) {
      return;
    }

    // Preview mode - show immediately
    if (this.preview) {
      setTimeout(() => this.show(), 500);
      this.setupEmailForm();
      this.setupSpinWheel();
      return;
    }

    // Normal mode - setup exit intent
    this.setupExitIntent();
    this.setupEmailForm();
    this.setupSpinWheel();

    // Check if form was already submitted (page reload after form post)
    if (window.location.search.includes('customer_posted=true')) {
      this.unlockWheel();
    }
  }

  /**
   * Check if popup should be shown
   * @returns {boolean}
   */
  shouldShow() {
    if (!this.enabled) {
      return false;
    }

    // Check cookie cooldown
    const cookieData = this.getCookie('figuro_spin_to_win');
    if (cookieData) {
      const { timestamp } = this.parseCookie(cookieData);
      const daysSince = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);

      if (daysSince < this.cooldownDays) {
        console.log(`Spin to Win: Cooldown active. ${Math.ceil(this.cooldownDays - daysSince)} days remaining.`);
        return false;
      }
    }

    return true;
  }

  /**
   * Setup exit intent detection
   */
  setupExitIntent() {
    const isMobile = window.innerWidth < 1025;

    if (isMobile) {
      this.setupMobileExitIntent();
    } else {
      this.setupDesktopExitIntent();
    }
  }

  /**
   * Desktop exit intent (mouseleave)
   */
  setupDesktopExitIntent() {
    const delay = this.delayDesktop * 1000;
    let isEnabled = false;

    // Enable exit intent after delay
    setTimeout(() => {
      isEnabled = true;
    }, delay);

    // Detect mouse leaving viewport
    document.addEventListener('mouseleave', (e) => {
      if (e.clientY <= 0 && isEnabled && !this.hasShown) {
        this.hasShown = true;
        this.show();
      }
    });
  }

  /**
   * Mobile exit intent (scroll + time-based)
   */
  setupMobileExitIntent() {
    const delay = this.delayMobile * 1000;
    let lastScrollY = window.scrollY;

    // Time-based trigger (primary)
    setTimeout(() => {
      if (!this.hasShown) {
        this.hasShown = true;
        this.show();
      }
    }, delay);

    // Scroll up detection (backup trigger)
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // User scrolled up rapidly (>50px) and is past 300px mark
      if (currentScrollY < lastScrollY - 50 && currentScrollY > 300 && !this.hasShown) {
        this.hasShown = true;
        this.show();
        window.removeEventListener('scroll', handleScroll);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
  }

  /**
   * Setup email form handling
   */
  setupEmailForm() {
    const form = this.querySelector('#spin-email-form');
    if (!form) return;

    // Track email submission
    form.addEventListener('submit', (e) => {
      const emailInput = form.querySelector('input[type="email"]');
      if (emailInput) {
        this.trackEvent('spin_email_submitted', {
          email: emailInput.value,
          timestamp: Date.now()
        });
      }
    });
  }

  /**
   * Unlock wheel after email submission
   */
  unlockWheel() {
    this.wheelUnlocked = true;

    // Remove locked class and overlay
    if (this.lockOverlay) {
      this.lockOverlay.style.display = 'none';
    }

    const wheel = this.querySelector('[data-spin-wheel]');
    if (wheel) {
      wheel.classList.remove('locked');
    }

    // Enable spin button
    if (this.spinButton) {
      this.spinButton.disabled = false;
    }

    // Show unlocked message, hide form
    if (this.formContainer) {
      this.formContainer.style.display = 'none';
    }
    if (this.unlockedMessage) {
      this.unlockedMessage.removeAttribute('hidden');
    }
  }

  /**
   * Setup spin wheel interaction
   */
  setupSpinWheel() {
    if (this.spinButton) {
      this.spinButton.addEventListener('click', this.handleSpin);
    }
  }

  /**
   * Handle spin button click
   */
  handleSpin() {
    if (!this.wheelUnlocked || this.isSpinning) {
      return;
    }

    this.spin();
  }

  /**
   * Get all prizes from data attributes
   * @returns {Array}
   */
  getPrizes() {
    const prizes = [];

    for (let i = 1; i <= 8; i++) {
      const label = this.dataset[`prize${i}Label`];
      const code = this.dataset[`prize${i}Code`];
      const weight = parseInt(this.dataset[`prize${i}Weight`]) || 0;
      const color = this.dataset[`prize${i}Color`];

      if (label && code && weight > 0) {
        prizes.push({ id: i, label, code, weight, color });
      }
    }

    return prizes;
  }

  /**
   * Select prize using weighted random selection
   * @returns {Object}
   */
  selectPrize() {
    const prizes = this.getPrizes();

    if (prizes.length === 0) {
      console.error('Spin to Win: No prizes configured');
      return null;
    }

    const totalWeight = prizes.reduce((sum, p) => sum + p.weight, 0);
    let random = Math.random() * totalWeight;

    for (const prize of prizes) {
      random -= prize.weight;
      if (random <= 0) {
        return prize;
      }
    }

    return prizes[0]; // Fallback
  }

  /**
   * Spin the wheel
   */
  spin() {
    this.isSpinning = true;
    const prize = this.selectPrize();

    if (!prize) {
      console.error('Spin to Win: Prize selection failed');
      return;
    }

    // Disable button
    if (this.spinButton) {
      this.spinButton.disabled = true;
    }

    // Calculate rotation
    const segmentAngle = 360 / 8; // 45 degrees per segment
    const prizeAngle = (prize.id - 1) * segmentAngle;
    const randomOffset = Math.random() * 10 - 5; // ±5 degrees for natural variation
    const spins = 4; // Number of full rotations
    const finalRotation = (360 * spins) + (360 - prizeAngle) + randomOffset;

    // Apply rotation
    if (this.wheelContainer) {
      this.wheelContainer.style.transform = `rotate(${finalRotation}deg)`;
    }

    // Track spin event
    this.trackEvent('spin_wheel_spun', {
      timestamp: Date.now()
    });

    // Announce to screen readers
    const srSpinning = this.querySelector('[data-sr-spinning]');
    if (srSpinning) {
      srSpinning.removeAttribute('hidden');
    }

    // Wait for animation to complete
    setTimeout(() => {
      this.isSpinning = false;

      // Hide spinning announcement
      if (srSpinning) {
        srSpinning.setAttribute('hidden', '');
      }

      // Show result
      this.showPrizeResult(prize);

      // Fire confetti
      this.fireConfetti();

      // Set cookie
      this.setCookie('figuro_spin_to_win', `${Date.now()}_${prize.code}`, this.cooldownDays);

      // Announce result to screen readers
      const srResult = this.querySelector('[data-sr-result]');
      if (srResult) {
        srResult.textContent = `Congratulations! You won ${prize.label}. Your discount code is ${prize.code}.`;
        srResult.removeAttribute('hidden');
      }
    }, 4000); // Match CSS transition duration
  }

  /**
   * Show prize result modal
   * @param {Object} prize
   */
  showPrizeResult(prize) {
    if (!this.resultModal) return;

    // Set prize label
    const labelElement = this.querySelector('[data-result-label]');
    if (labelElement) {
      labelElement.textContent = prize.label;
    }

    // Set discount code
    const codeInput = this.querySelector('[data-result-code]');
    if (codeInput) {
      codeInput.value = prize.code;
    }

    // Set shop link with discount
    const link = this.querySelector('[data-result-link]');
    if (link) {
      link.href = `${prize.code}`;

      // Track discount application
      link.addEventListener('click', () => {
        this.trackEvent('spin_discount_applied', {
          code: prize.code,
          timestamp: Date.now()
        });
      }, { once: true });
    }

    // Show result modal
    this.resultModal.removeAttribute('hidden');

    // Track prize won
    this.trackEvent('spin_prize_won', {
      prize: prize.label,
      code: prize.code,
      timestamp: Date.now()
    });
  }

  /**
   * Fire confetti animation
   */
  fireConfetti() {
    // Check if confetti library is loaded
    if (typeof confetti === 'undefined') {
      console.warn('Spin to Win: Confetti library not loaded');
      return;
    }

    const duration = 3000;
    const end = Date.now() + duration;
    const colors = ['#5B4CFF', '#7B6EFF', '#EDE9FF']; // Figuro brand colors

    const interval = setInterval(() => {
      if (Date.now() > end) {
        clearInterval(interval);
        return;
      }

      // Fire from left
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: colors
      });

      // Fire from right
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: colors
      });
    }, 250);
  }

  /**
   * Show the popup
   */
  show() {
    setTimeout(() => {
      this.classList.add('active');
      this.classList.add('animate');
    }, 10);

    // Manage body overflow
    if (document.querySelectorAll('.popup-modal.active').length === 0) {
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
    }

    // Track popup shown
    this.trackEvent('spin_popup_shown', {
      timestamp: Date.now(),
      page: window.location.pathname
    });
  }

  /**
   * Close the popup
   */
  close() {
    const stage = this.wheelUnlocked
      ? (this.resultModal && !this.resultModal.hidden ? 'prize_shown' : 'wheel_unlocked')
      : 'email_form';

    this.classList.remove('animate');

    setTimeout(() => {
      this.classList.remove('active');

      if (document.querySelectorAll('.popup-modal.active').length === 0) {
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
    }, 500);

    // Track closure
    this.trackEvent('spin_popup_closed', {
      stage: stage,
      timestamp: Date.now()
    });
  }

  /**
   * Get scrollbar width (for body padding compensation)
   * @returns {number}
   */
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /**
   * Get cookie value
   * @param {string} name
   * @returns {string}
   */
  getCookie(name) {
    return document.cookie.split('; ').reduce((r, c) => {
      const [key, val] = c.split('=');
      return key === name ? decodeURIComponent(val) : r;
    }, '');
  }

  /**
   * Set cookie
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /**
   * Parse cookie value
   * @param {string} value
   * @returns {Object}
   */
  parseCookie(value) {
    const [timestamp, code] = value.split('_');
    return {
      timestamp: parseInt(timestamp) || Date.now(),
      code: code || ''
    };
  }

  /**
   * Track event (GA4 + Custom Event)
   * @param {string} eventName
   * @param {Object} data
   */
  trackEvent(eventName, data) {
    // Always fire custom event
    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));

    // Fire GA4 event if enabled and available
    if (this.enableAnalytics && typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'spin_to_win',
        event_label: data.prize || data.email || data.stage || 'interaction',
        value: data.code ? 1 : 0,
        ...data
      });
    }
  }
}

// Define custom element
customElements.define('spin-to-win-popup', SpinToWinPopup);